import type { RaceHistorySummary } from '@multirx/shared/types/raceHistory.js';
import { nationalityFlagMarkup } from '../components/nationalityFlag.js';
import { driverInitials } from '../game/playerProfileTooltip.js';
import { resolveMediaUrl } from '../mediaUrl.js';

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

function formatTooltipDate(iso?: string): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));
}

function participantAvatarHtml(p: { driverName: string; driverImagePath?: string | null }): string {
  if (p.driverImagePath) {
    return `<img class="archives-list-tooltip-avatar" src="${escapeHtml(resolveMediaUrl(p.driverImagePath))}" alt="" loading="lazy" />`;
  }
  return `<span class="archives-list-tooltip-avatar archives-list-tooltip-avatar--initials" aria-hidden="true">${escapeHtml(driverInitials(p.driverName))}</span>`;
}

function participantCarThumbHtml(p: { carName: string; carImagePath?: string | null }): string {
  if (!p.carImagePath) return '';
  const name = escapeHtml(p.carName || 'Voiture');
  return `<img class="archives-list-tooltip-car-thumb" src="${escapeHtml(resolveMediaUrl(p.carImagePath))}" alt="${name}" title="${name}" loading="lazy" />`;
}

function participantRowHtml(p: {
  driverName: string;
  carName: string;
  driverImagePath?: string | null;
  carImagePath?: string | null;
  nationality?: string;
}): string {
  const flag = p.nationality
    ? nationalityFlagMarkup(p.nationality, { className: 'archives-list-tooltip-flag' })
    : '';
  const carName = escapeHtml(p.carName || '—');
  return `
    <li class="archives-list-tooltip-participant">
      ${participantAvatarHtml(p)}
      <span class="archives-list-tooltip-participant-main">
        <span class="archives-list-tooltip-driver-line">
          ${flag}
          <span class="archives-list-tooltip-driver">${escapeHtml(p.driverName)}</span>
        </span>
        <span class="archives-list-tooltip-car">${carName}</span>
      </span>
      ${participantCarThumbHtml(p)}
    </li>`;
}

export function buildArchiveListTooltipHtml(item: RaceHistorySummary): string {
  const roster = item.participants?.length
    ? item.participants.map(participantRowHtml).join('')
    : `<li class="archives-list-tooltip-muted">${item.numParticipants} pilote(s) — détail non enregistré</li>`;

  const date = formatTooltipDate(item.createdAt);

  return `
    <div class="archives-list-tooltip-inner">
      <p class="archives-list-tooltip-title">
        Course #${item.id}
        <span class="archives-list-tooltip-date">(${escapeHtml(date)})</span>
      </p>
      <ul class="archives-list-tooltip-roster">${roster}</ul>
    </div>
  `;
}

const TOOLTIP_GAP = 10;
let listTooltipEl: HTMLDivElement | null = null;
let listTooltipAnchor: HTMLElement | null = null;

function getListTooltipEl(): HTMLDivElement {
  if (!listTooltipEl) {
    listTooltipEl = document.createElement('div');
    listTooltipEl.className = 'archives-list-tooltip';
    listTooltipEl.hidden = true;
    document.body.appendChild(listTooltipEl);
  }
  return listTooltipEl;
}

function positionListTooltip(tip: HTMLDivElement, anchor: HTMLElement): void {
  tip.hidden = false;
  const anchorRect = anchor.getBoundingClientRect();
  const tipRect = tip.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const pad = 8;

  let left = anchorRect.left + anchorRect.width / 2 - tipRect.width / 2;
  let top = anchorRect.bottom + TOOLTIP_GAP;
  tip.classList.toggle('is-above-anchor', false);

  if (top + tipRect.height > vh - pad) {
    top = anchorRect.top - tipRect.height - TOOLTIP_GAP;
    tip.classList.add('is-above-anchor');
  }

  left = Math.max(pad, Math.min(left, vw - tipRect.width - pad));
  top = Math.max(pad, Math.min(top, vh - tipRect.height - pad));

  tip.style.left = `${Math.round(left)}px`;
  tip.style.top = `${Math.round(top)}px`;
}

export function showArchiveListTooltip(anchor: HTMLElement, html: string): void {
  const tip = getListTooltipEl();
  listTooltipAnchor = anchor;
  tip.innerHTML = html;
  tip.hidden = false;
  requestAnimationFrame(() => {
    if (listTooltipAnchor === anchor) positionListTooltip(tip, anchor);
  });
}

export function hideArchiveListTooltip(): void {
  listTooltipAnchor = null;
  if (listTooltipEl) listTooltipEl.hidden = true;
}

export function removeArchiveListTooltip(): void {
  hideArchiveListTooltip();
  listTooltipEl?.remove();
  listTooltipEl = null;
}
